import { JSX, useEffect, useState, memo } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { Box, Stack, Table, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { CandidateWithJobContext } from '@/types/Candidate';

interface ChangingProgressProviderProps {
  values: number[];
  interval?: number;
  children: (value: number) => JSX.Element;
}

interface CircleProgressBarProps {
  value: number;
  interval?: number;
  tooltipContent?: string;
  candidate?: CandidateWithJobContext;
}

interface ScoreDetailsProps {
  candidate?: CandidateWithJobContext;
}

const DEFAULT_INTERVAL = 50;

const ChangingProgressProvider = memo(
  ({ values, interval = DEFAULT_INTERVAL, children }: ChangingProgressProviderProps) => {
    const [valuesIndex, setValuesIndex] = useState(0);

    useEffect(() => {
      if (valuesIndex < values.length - 1) {
        const intervalId = setInterval(() => {
          setValuesIndex((prevIndex) => prevIndex + 1);
        }, interval);

        return () => clearInterval(intervalId);
      }
    }, [values, interval, valuesIndex]);

    return children(values[valuesIndex]);
  }
);

ChangingProgressProvider.displayName = 'ChangingProgressProvider';

const ScoreDetails = memo(({ candidate }: ScoreDetailsProps) => (
  <Stack gap="4px" p="8px" justifyContent="flex-start" alignItems="flex-start">
    <Text fontSize="14px" fontWeight="semibold">
      Score Details
    </Text>
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader> </Table.ColumnHeader>
          <Table.ColumnHeader>System</Table.ColumnHeader>
          <Table.ColumnHeader>User</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <Table.Row>
          <Table.Cell>Resume</Table.Cell>
          <Table.Cell>{candidate?.resume_analysis_result?.fit_score.score ?? 'N/A'}</Table.Cell>
          <Table.Cell>{candidate?.user_score?.resume_score ?? 'N/A'}</Table.Cell>
        </Table.Row>
        {(candidate?.interview_analysis_result || candidate?.screening_analysis_result) && (
          <>
            <Table.Row>
              <Table.Cell>Interview</Table.Cell>
              <Table.Cell>
                {candidate?.interview_analysis_result
                  ? candidate.interview_analysis_result.score.score
                  : candidate?.screening_analysis_result
                  ? candidate.screening_analysis_result.score.score
                  : 'N/A'}
              </Table.Cell>
              <Table.Cell>{candidate?.user_score?.interview_score ?? 'N/A'}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Overview</Table.Cell>
              <Table.Cell fontWeight="semibold">{candidate?.system_score ?? 'N/A'}</Table.Cell>
              <Table.Cell fontWeight="semibold">
                {candidate?.user_score?.overall_score ?? 'N/A'}
              </Table.Cell>
            </Table.Row>
          </>
        )}
      </Table.Body>
    </Table.Root>
  </Stack>
));

ScoreDetails.displayName = 'ScoreDetails';

const CircleProgressBar = ({
  value,
  interval = DEFAULT_INTERVAL,
  tooltipContent,
  candidate
}: CircleProgressBarProps) => {
  const [open, setOpen] = useState(false);

  const progressBar = (
    <Box cursor="pointer">
      <ChangingProgressProvider values={[0, value]} interval={interval}>
        {(percentage) => (
          <CircularProgressbar
            value={percentage}
            text={`${percentage}`}
            circleRatio={0.8}
            strokeWidth={15}
            styles={buildStyles({
              rotation: 0.6,
              strokeLinecap: 'round',
              textSize: '28px',
              pathColor: '#829FFF',
              trailColor: '#CAD6FF',
              textColor: '#3C5CC6'
            })}
          />
        )}
      </ChangingProgressProvider>
    </Box>
  );

  if (!tooltipContent && !candidate) {
    return progressBar;
  }

  return (
    <Box cursor="pointer" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Tooltip
        content={tooltipContent || <ScoreDetails candidate={candidate} />}
        open={open}
        contentProps={{
          bg: 'neutrals.0',
          color: 'neutrals.900',
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'neutrals.200',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          p: '8px',
          fontSize: '14px'
        }}
      >
        {progressBar}
      </Tooltip>
    </Box>
  );
};

export default CircleProgressBar;
